import dayjs from "dayjs";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import {
  BETWEEN,
  DATE,
  GTE,
  LIKE,
  LTE,
  STRING,
  BOOL,
  EQ,
} from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import DatePicker from "../../framework/date/DatePicker";
import GlobalContext from "../../framework/GlobalContext";
import NumberInput from "../../framework/NumberInput";
import { executeScroll, isEmpty } from "../../utils/utils";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import SelectInput from "../../framework/SelectInput";
const DiscountProgramReportFilter = ({
  onFilterShow,
  applyFilterOnMount = true,
  reportRef,
}) => {
  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [programName, setProgramName] = useState("");
  const [status, setStatus] = useState("All");
  const [limit, setLimit] = useState(100);
  const filterProgramNameRef = useRef();
  const filterStartDateRef = useRef();
  const filterEndDateRef = useRef();
  const filterStatusRef = useRef();
  const filterLimitRef = useRef();
  const [orderBy] = useState([{ fieldName: "createdTime", orderType: "desc" }]);

  const statusOptions = useMemo(() => ["All", "Active", "Inactive"], []);

  const { setCurrentComponent } = useContext(GlobalContext);

  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      const _startDate = startDate?.format("DD/MM/YYYY");
      const _endDate = endDate?.format("DD/MM/YYYY");
      if (_startDate && !_endDate) {
        filters.push(createFilterParam("createdTime", _startDate, DATE, GTE));
      }
      if (!_startDate && _endDate) {
        filters.push(createFilterParam("createdTime", _endDate, DATE, LTE));
      }
      if (_startDate && _endDate) {
        filters.push(
          createFilterParam(
            "createdTime",
            { start: _startDate, end: _endDate },
            DATE,
            BETWEEN
          )
        );
      }
      if (programName) {
        filters.push(
          createFilterParam("programName", programName, STRING, LIKE)
        );
      }
      if (status && status !== "All") {
        filters.push(
          createFilterParam(
            "deleted",
            status === "Active" ? false : true,
            BOOL,
            EQ
          )
        );
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;

      if (orderBy) {
        result.orderBy = orderBy;
      }
      if (!isEmpty(limit)) {
        result.limit = limit;
      }

      reportRef.current?.applyFilters(result);
    },
    [endDate, limit, orderBy, programName, reportRef, startDate, status]
  );

  const filterListener = useCallback(
    (event) => {
      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey;
      if (ctrl && key === "enter") {
        applyFilterHandler(true);
        return;
      }
    },
    [applyFilterHandler]
  );
  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <DatePicker
            id="dsr_filter_start_date"
            label="Start Date"
            inputRef={filterStartDateRef}
            onChange={(value) => setStartDate(value)}
            onFocus={() => setCurrentComponent("dsr_filter_start_date")}
            value={startDate}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "dsr_filter_start_date",
                down: "dsr_filter_end_date",
                left: "dsr_filter_start_date",
                right: "dsr_filter_end_date",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <DatePicker
            id="dsr_filter_end_date"
            label="End Date"
            inputRef={filterEndDateRef}
            onChange={(value) => setEndDate(value)}
            onFocus={() => {
              executeScroll(filterEndDateRef);
              setCurrentComponent("dsr_filter_end_date");
            }}
            value={endDate}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "dsr_filter_start_date",
                down: "dsr_filter_program_name",
                left: "dsr_filter_start_date",
                right: "dsr_filter_program_name",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="dsr_filter_program_name"
            label="Program Name"
            ref={filterProgramNameRef}
            value={programName}
            onChange={(e) => setProgramName(e.target.value)}
            onFocus={() => {
              executeScroll(filterProgramNameRef);
              setCurrentComponent("dsr_filter_program_name");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "dsr_filter_end_date",
                down: "dsr_filter_status",
                left: "dsr_filter_end_date",
                right: "dsr_filter_status",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="dsr_filter_status"
            label="Status"
            ref={filterStatusRef}
            options={statusOptions.map((status) => ({
              value: status,
              label: status,
            }))}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            isOptionEqualToValue={(option, value) => option.value === value}
            value={status}
            onChange={(e, option) => setStatus(option?.value || "")}
            onFocus={() => {
              executeScroll(filterStatusRef);
              setCurrentComponent("dsr_filter_status");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "dsr_filter_program_name",
              down: "dsr_filter_limit",
              left: "dsr_filter_program_name",
              right: "dsr_filter_limit",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="dsr_filter_limit"
            label="Limit"
            ref={filterLimitRef}
            onValueChange={(values) => setLimit(values.floatValue)}
            isAllowed={(values) => values?.formattedValue <= 999999}
            onFocus={() => {
              executeScroll(filterLimitRef);
              setCurrentComponent("dsr_filter_limit");
            }}
            value={limit}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "dsr_filter_status",
                down: "dsr_apply_filter_button",
                left: "dsr_filter_status",
                right: "dsr_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="dsr_apply_filter_button"
            label="Apply"
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => applyFilterHandler(true)}
            markers={JSON.stringify({
              up: "dsr_filter_limit",
              left: "dsr_filter_limit",
              down: "dsr_apply_filter_button",
              right: "dsr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  useEffect(() => {
    if (applyFilterOnMount) applyFilterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilterOnMount]);

  useEffect(() => {
    if (onFilterShow) {
      filterStartDateRef?.current?.focus({ preventScroll: true });
    }
  }, [onFilterShow]);

  useEffect(() => {
    if (onFilterShow) {
      document.addEventListener("keyup", filterListener);
    }
    return () => {
      document.removeEventListener("keyup", filterListener);
    };
  }, [filterListener, onFilterShow]);

  return (
    <Filters
      title="Discount Program Report Filters"
      filterContent={filterContent}
      filterFooter={filterFooter}
    />
  );
};

export default DiscountProgramReportFilter;
